import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Heart, Clock, Euro, Users, Plus, ArrowLeft, Sparkles, Zap, Target, Building, Loader2, TrendingUp, BarChart3, Calculator, Calendar } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SubmitUseCaseModal from '../components/SubmitUseCaseModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useCasesAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

// Filter options with translations
const getCategories = (language) => [
  { value: 'all', label: language === 'nl' ? 'Alle Categorieën' : 'All Categories' },
  { value: 'quick-wins', label: 'Quick Wins' },
  { value: 'foundational', label: language === 'nl' ? 'Fundamenteel' : 'Foundational' },
  { value: 'strategic', label: language === 'nl' ? 'Strategisch' : 'Strategic' },
];

const getDomains = (language) => [
  { value: 'all', label: language === 'nl' ? 'Alle domeinen' : 'All domains' },
  { value: 'Recruitment', label: language === 'nl' ? 'Werving' : 'Recruitment' },
  { value: 'Learning & Development', label: language === 'nl' ? 'Leren & Ontwikkeling' : 'Learning & Development' },
  { value: 'Talent Management', label: language === 'nl' ? 'Talentmanagement' : 'Talent Management' },
  { value: 'Performance', label: language === 'nl' ? 'Prestaties' : 'Performance' },
  { value: 'Wellbeing', label: language === 'nl' ? 'Welzijn' : 'Wellbeing' },
  { value: 'Onboarding', label: 'Onboarding' },
  { value: 'HR Operations', label: language === 'nl' ? 'HR Operaties' : 'HR Operations' },
];

const getDifficulties = (language) => [
  { value: 'all', label: language === 'nl' ? 'Alle niveaus' : 'All levels' },
  { value: 'low', label: language === 'nl' ? 'Laag (Quick Win)' : 'Low (Quick Win)' },
  { value: 'medium', label: language === 'nl' ? 'Gemiddeld' : 'Medium' },
  { value: 'high', label: language === 'nl' ? 'Hoog (Strategisch)' : 'High (Strategic)' },
];

const getImpacts = (language) => [
  { value: 'all', label: language === 'nl' ? 'Alle impact' : 'All impact' },
  { value: 'low', label: language === 'nl' ? 'Laag' : 'Low' },
  { value: 'medium', label: language === 'nl' ? 'Gemiddeld' : 'Medium' },
  { value: 'high', label: language === 'nl' ? 'Hoog' : 'High' },
];

const getSortOptions = (language) => [
  { value: 'popular', label: language === 'nl' ? 'Meest populair' : 'Most Popular' },
  { value: 'impact', label: language === 'nl' ? 'Hoogste impact' : 'Highest Impact' },
  { value: 'quick', label: language === 'nl' ? 'Quick Wins eerst' : 'Quick Wins First' },
];

// Translation helper for domain labels
const translateDomain = (domain, language) => {
  const translations = {
    'Recruitment': language === 'nl' ? 'Werving' : 'Recruitment',
    'Learning & Development': language === 'nl' ? 'Leren & Ontwikkeling' : 'Learning & Development',
    'Talent Management': language === 'nl' ? 'Talentmanagement' : 'Talent Management',
    'Performance': language === 'nl' ? 'Prestaties' : 'Performance',
    'Wellbeing': language === 'nl' ? 'Welzijn' : 'Wellbeing',
    'Onboarding': 'Onboarding',
    'HR Operations': language === 'nl' ? 'HR Operaties' : 'HR Operations',
  };
  return translations[domain] || domain;
};

// Translation helper for difficulty levels
const translateDifficulty = (difficulty, language) => {
  const translations = {
    'low': language === 'nl' ? 'Laag' : 'Low',
    'medium': language === 'nl' ? 'Gemiddeld' : 'Medium',
    'high': language === 'nl' ? 'Hoog' : 'High',
    'Low': language === 'nl' ? 'Laag' : 'Low',
    'Medium': language === 'nl' ? 'Gemiddeld' : 'Medium',
    'High': language === 'nl' ? 'Hoog' : 'High',
  };
  return translations[difficulty] || difficulty;
};

// Translation helper for category
const translateCategory = (category, language) => {
  const translations = {
    'quick-wins': 'Quick Wins',
    'foundational': language === 'nl' ? 'Fundamenteel' : 'Foundational',
    'strategic': language === 'nl' ? 'Strategisch' : 'Strategic',
  };
  return translations[category] || category;
};

const UseCasesPage = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [useCases, setUseCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedImpact, setSelectedImpact] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [votedCases, setVotedCases] = useState(new Set());
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [votingInProgress, setVotingInProgress] = useState(new Set());

  // Get translated filter options
  const categories = getCategories(language);
  const domains = getDomains(language);
  const difficulties = getDifficulties(language);
  const impacts = getImpacts(language);
  const sortOptions = getSortOptions(language);

  // Initialize voted cases from localStorage
  useEffect(() => {
    const storedVotes = JSON.parse(localStorage.getItem('votedUseCases') || '[]');
    setVotedCases(new Set(storedVotes));
  }, []);

  // Helper to get translated text
  const getTranslatedText = (uc, field) => {
    const nlField = `${field}_nl`;
    if (language === 'nl' && uc[nlField]) {
      return uc[nlField];
    }
    return uc[field];
  };

  // Fetch use cases from API
  useEffect(() => {
    const fetchUseCases = async () => {
      setLoading(true);
      try {
        const data = await useCasesAPI.getAll();
        if (data && data.length > 0) {
          // Enrich with labels if needed
          const enriched = data.map(uc => ({
            ...uc,
            domainLabel: uc.domainLabel || uc.domain,
            categoryLabel: getCategoryConfig(uc.category).label
          }));
          setUseCases(enriched);
        }
      } catch (error) {
        console.error('Failed to fetch use cases:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUseCases();
  }, []);

  const filteredUseCases = useCases
    .filter((uc) => {
      const title = getTranslatedText(uc, 'title');
      const description = getTranslatedText(uc, 'description');
      const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || uc.category === selectedCategory || uc.secondaryCategory === selectedCategory;
      const matchesDomain = selectedDomain === 'all' || uc.domain === selectedDomain;
      const matchesDifficulty = selectedDifficulty === 'all' || uc.difficulty === selectedDifficulty;
      const matchesImpact = selectedImpact === 'all' || uc.impact === selectedImpact;
      return matchesSearch && matchesCategory && matchesDomain && matchesDifficulty && matchesImpact;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'impact':
          const impactOrder = { high: 3, medium: 2, low: 1 };
          return impactOrder[b.impact] - impactOrder[a.impact];
        case 'quick':
          const difficultyOrder = { low: 1, medium: 2, high: 3 };
          return difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
        default:
          return b.votes - a.votes;
      }
    });

  const handleVote = async (e, id) => {
    // Stop event from bubbling up to parent elements
    e.preventDefault();
    e.stopPropagation();
    
    if (votingInProgress.has(id)) return;
    
    setVotingInProgress(prev => new Set([...prev, id]));
    
    try {
      const hasVoted = votedCases.has(id);
      
      if (hasVoted) {
        // Unvote
        const result = await useCasesAPI.unvote(id);
        setUseCases((prev) =>
          prev.map((uc) => (uc.id === id ? { ...uc, votes: result.votes } : uc))
        );
        const newVotedCases = new Set(votedCases);
        newVotedCases.delete(id);
        setVotedCases(newVotedCases);
        
        // Update localStorage
        const storedVotes = JSON.parse(localStorage.getItem('votedUseCases') || '[]');
        const newStoredVotes = storedVotes.filter(caseId => caseId !== id);
        localStorage.setItem('votedUseCases', JSON.stringify(newStoredVotes));
      } else {
        // Vote
        const result = await useCasesAPI.vote(id);
        setUseCases((prev) =>
          prev.map((uc) => (uc.id === id ? { ...uc, votes: result.votes } : uc))
        );
        setVotedCases((prev) => new Set([...prev, id]));
        
        // Update localStorage
        const storedVotes = JSON.parse(localStorage.getItem('votedUseCases') || '[]');
        storedVotes.push(id);
        localStorage.setItem('votedUseCases', JSON.stringify(storedVotes));
      }
    } catch (error) {
      console.error('Failed to vote:', error);
    } finally {
      setVotingInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const getCategoryConfig = (category) => {
    switch (category) {
      case 'quick-wins':
        return { label: 'QUICK WIN', bg: 'bg-green-500', icon: Zap };
      case 'strategic':
        return { label: 'STRATEGIC', bg: 'bg-purple-500', icon: Target };
      case 'foundational':
        return { label: 'FOUNDATIONAL', bg: 'bg-blue-500', icon: Building };
      default:
        return { label: category.toUpperCase(), bg: 'bg-gray-500', icon: Sparkles };
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'low': return 'text-blue-600';
      case 'medium': return 'text-blue-600';
      case 'high': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'text-blue-600';
      case 'medium': return 'text-blue-600';
      case 'low': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <p className="text-gray-600">
              <span className="font-semibold text-gray-900">{filteredUseCases.length}</span> {language === 'nl' ? 'toepassingen gevonden' : 'use cases found'}
            </p>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/roi-compare')}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                {language === 'nl' ? 'ROI Vergelijker' : 'ROI Comparer'}
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{language === 'nl' ? 'Sorteer op:' : 'Sort by:'}</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-36 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <span className="text-blue-500">●</span> {language === 'nl' ? 'Categorie' : 'Category'}
              </label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder={language === 'nl' ? 'Alle Categorieën' : 'All Categories'} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'nl' ? 'HR Domein' : 'HR Domain'}</label>
              <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder={language === 'nl' ? 'Alle domeinen' : 'All domains'} />
                </SelectTrigger>
                <SelectContent>
                  {domains.map((domain) => (
                    <SelectItem key={domain.value} value={domain.value}>
                      {domain.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'nl' ? 'Moeilijkheid' : 'Difficulty'}</label>
              <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder={language === 'nl' ? 'Alle niveaus' : 'All levels'} />
                </SelectTrigger>
                <SelectContent>
                  {difficulties.map((diff) => (
                    <SelectItem key={diff.value} value={diff.value}>
                      {diff.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'nl' ? 'Bedrijfsimpact' : 'Business Impact'}</label>
              <Select value={selectedImpact} onValueChange={setSelectedImpact}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder={language === 'nl' ? 'Alle impact' : 'All impact'} />
                </SelectTrigger>
                <SelectContent>
                  {impacts.map((impact) => (
                    <SelectItem key={impact.value} value={impact.value}>
                      {impact.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'nl' ? 'Zoeken' : 'Search'}</label>
              <Input
                type="text"
                placeholder={language === 'nl' ? 'Zoeken...' : 'Search...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-white text-blue-500 placeholder:text-blue-300"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="mb-8">
            <Button 
              onClick={() => setShowSubmitModal(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white gap-2"
            >
              <Plus className="h-4 w-4" />
              {language === 'nl' ? 'Dien je eigen use case in' : 'Submit your own use case'}
            </Button>
          </div>

          {/* Submit Use Case Modal */}
          <SubmitUseCaseModal 
            isOpen={showSubmitModal} 
            onClose={() => setShowSubmitModal(false)} 
          />

          {/* Use Cases Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredUseCases.map((useCase) => {
                const categoryConfig = getCategoryConfig(useCase.category);
                const CategoryIcon = categoryConfig.icon;
                const title = getTranslatedText(useCase, 'title');
                const shortDesc = getTranslatedText(useCase, 'shortDescription');
                return (
                  <div
                    key={useCase.id}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    {/* Category Badge(s) — dual badge when secondaryCategory exists */}
                    {useCase.secondaryCategory ? (
                      <div className="flex">
                        <div className={`${categoryConfig.bg} text-white text-xs font-bold py-2 px-3 flex items-center justify-center gap-1.5 flex-1`}>
                          <CategoryIcon className="h-3.5 w-3.5" />
                          {categoryConfig.label}
                        </div>
                        {(() => {
                          const secConfig = getCategoryConfig(useCase.secondaryCategory);
                          const SecIcon = secConfig.icon;
                          return (
                            <div className={`${secConfig.bg} text-white text-xs font-bold py-2 px-3 flex items-center justify-center gap-1.5 flex-1 border-l border-white/30`}>
                              <SecIcon className="h-3.5 w-3.5" />
                              {secConfig.label}
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className={`${categoryConfig.bg} text-white text-xs font-bold py-2 px-4 flex items-center justify-center gap-2`}>
                        <CategoryIcon className="h-4 w-4" />
                        {categoryConfig.label}
                      </div>
                    )}

                    <div className="p-5">
                      {/* Title & Votes */}
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-bold text-gray-900">
                          {title}
                        </h3>
                        <button
                          onClick={(e) => handleVote(e, useCase.id)}
                          disabled={votingInProgress.has(useCase.id)}
                          className={`flex items-center gap-1 transition-colors ${
                            votedCases.has(useCase.id) ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
                          } ${votingInProgress.has(useCase.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                          data-testid={`vote-btn-${useCase.id}`}
                        >
                          <Heart className={`h-5 w-5 ${votedCases.has(useCase.id) ? 'fill-current' : ''}`} />
                          <span className="text-sm font-medium">{useCase.votes}</span>
                        </button>
                      </div>

                      {/* Description */}
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {shortDesc}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
                          <Building className="h-3 w-3" />
                          {translateDomain(useCase.domain || useCase.domainLabel, language)}
                        </span>
                        <span className={`px-3 py-1 text-xs font-medium bg-gray-100 rounded-full flex items-center gap-1 ${getDifficultyColor(useCase.difficulty)}`}>
                          <BarChart3 className="h-3 w-3" />
                          {translateDifficulty(useCase.difficulty, language)}
                        </span>
                        <span className={`px-3 py-1 text-xs font-medium bg-gray-100 rounded-full flex items-center gap-1 ${getImpactColor(useCase.impact)}`}>
                          <TrendingUp className="h-3 w-3" />
                          {useCase.impact === 'high' ? (language === 'nl' ? 'Hoge' : 'High') :
                           useCase.impact === 'medium' ? (language === 'nl' ? 'Gemiddelde' : 'Medium') :
                           (language === 'nl' ? 'Lage' : 'Low')} {language === 'nl' ? 'impact' : 'impact'}
                        </span>
                      </div>

                      {/* Metrics */}
                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                        <div className="flex items-center gap-1">
                          <Euro className="h-3 w-3" />
                          <span>{useCase.cost || (language === 'nl' ? 'n.v.t.' : 'N/A')}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>{useCase.impl_time || useCase.implTime || (language === 'nl' ? 'n.v.t.' : 'N/A')} {language === 'nl' ? 'impl.' : 'impl.'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          <span>{useCase.skills || (language === 'nl' ? 'n.v.t.' : 'N/A')} {language === 'nl' ? 'vaardigheden' : 'skills'}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="space-y-2">
                        <Button
                          onClick={() => navigate(`/use-cases/${useCase.id}`)}
                          className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                        >
                          <ArrowLeft className="h-4 w-4 mr-2 rotate-180" />
                          {language === 'nl' ? 'Bekijk details' : 'View Full Details'}
                        </Button>
                        <Button
                          onClick={() => navigate(`/use-cases/${useCase.id}/workshop`)}
                          variant="outline"
                          className="w-full border-green-500 text-green-600 hover:bg-green-50"
                        >
                          <Calendar className="h-4 w-4 mr-2" />
                          {language === 'nl' ? 'Boek Workshop' : 'Book Workshop'}
                        </Button>
                        <Button
                          onClick={() => navigate(`/use-cases/${useCase.id}/roi`)}
                          variant="outline"
                          className="w-full border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                        >
                          <Calculator className="h-4 w-4 mr-2" />
                          {language === 'nl' ? 'Bereken ROI' : 'Calculate ROI'}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default UseCasesPage;
